import React from 'react';
import { useDispatch } from 'react-redux';
import { updateUserDoc } from '@services/docs';
import { showErrorBanner } from '@utils/application';

/*
  Rename and tagging both go through PATCH v1/genai_user_doc/{id}. The vault filters and groups
  off the returned document, so callers refresh the list rather than patching a local copy --
  a rename can move a document between groups, and a tag change certainly does.
*/
export function useDocumentMutations({ onAfterChange } = {}) {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const renameDocument = React.useCallback(
    async (doc, name) => {
      const trimmed = String(name ?? '').trim();
      if (!trimmed || trimmed === doc.filename) return { ok: false, unchanged: true };

      setIsSaving(true);
      setError(null);
      const result = await updateUserDoc(doc.id, { name: trimmed });
      setIsSaving(false);

      if (!result.ok) {
        setError(result.error);
        showErrorBanner(result.error, {}, dispatch);
        return result;
      }

      onAfterChange?.();
      return result;
    },
    [dispatch, onAfterChange]
  );

  const tagDocuments = React.useCallback(
    async (docs, docTagId) => {
      if (!docs?.length) return { ok: false, unchanged: true };

      setIsSaving(true);
      setError(null);

      // One request per document, sequentially: there is no bulk tag endpoint, and a burst of
      // parallel PATCHes against the same user buys nothing.
      const failed = [];
      for (const doc of docs) {
        const result = await updateUserDoc(doc.id, { doc_tag_id: docTagId });
        if (!result.ok) failed.push({ doc, error: result.error });
      }

      setIsSaving(false);
      onAfterChange?.();

      if (failed.length > 0) {
        const message =
          failed.length === docs.length
            ? 'Could not tag the selected documents.'
            : `Tagged ${docs.length - failed.length} of ${docs.length} documents.`;
        setError(message);
        showErrorBanner(message, {}, dispatch);
        return { ok: false, failed, succeeded: docs.length - failed.length };
      }

      return { ok: true, succeeded: docs.length };
    },
    [dispatch, onAfterChange]
  );

  return { renameDocument, tagDocuments, isSaving, error };
}
